import React from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import {
  Button, Tile, TileContent, TileText, Typography
} from '@one-thd/sui-atomic-components';
import { Filters, ArrowDown } from '@one-thd/sui-icons';
import { useConfigService } from '@thd-nucleus/experience-context';
import { PrimaryFiltersDrawer } from './PrimaryFiltersDrawer';
import { SquirclePill } from '../SquirclePill/SquirclePill';

const {
  func, string, arrayOf, shape, bool
} = proptypes;

const PrimaryFilters = ({
  dimensions,
  appliedDimensions,
  onRefinementChange,
  onCloseDrawer,
  baseUrl,
  canonicalUrl,
  primaryFilters,
  hideViewAll,
  isOpen,
  activeDimension,
  onSecondaryFilterDrawerToggle
}) => {
  const isPlpSpeedEnabled = useConfigService('fs-prop:isPlpSpeedEnabled');

  if (!dimensions) {
    return null;
  }
  if (dimensions.length === 0) {
    return null;
  }
  const renderPrimaryFiltersButton = (dimension) => {
    const commonProps = {
      key: dimension.dimensionId,
      'data-testid': 'primary-filters-btn',
      'aria-label': dimension.label
    };

    return isPlpSpeedEnabled ? (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <SquirclePill {...commonProps}>
        <Tile
          onClick={() => onCloseDrawer(true, dimension)}
          variant="outline"
        >
          <TileContent orientation="horizontal">
            <TileText>{dimension.label}</TileText>
          </TileContent>
        </Tile>
      </SquirclePill>
    ) : (
      <Button
        variant="ghost"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...commonProps}
        onClick={() => onCloseDrawer(true, dimension)}
      >
        <div className="sui-pr-2 sui-text-nowrap">
          <Typography variant="body-base" color="sui-bg-primary">{dimension.label}</Typography>
        </div>
        <ArrowDown size="small" />
      </Button>
    );
  };

  const renderFilterButtons = (primaryFiltersDimensions) => {
    if (primaryFiltersDimensions.length === 0) {
      return null;
    }
    return (
      <div className={classNames('sui-flex sui-gap-4', isPlpSpeedEnabled && 'sui-p-1')}>
        {!hideViewAll && (
          isPlpSpeedEnabled ? (
            <SquirclePill
              data-testid="all-filters-button"
              // eslint-disable-next-line @harmony/stencil/no-tailwind-disable
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              className="[&>button:not(:disabled,_:focus)]:sui-border-stronger [&_p]:sui-font-bold"
            >
              <Tile
                onClick={() => onSecondaryFilterDrawerToggle(true)}
                variant="outline"
              >
                <TileContent orientation="horizontal">
                  <Filters /> <TileText>All Filters</TileText>
                </TileContent>
              </Tile>
            </SquirclePill>
          ) : (
            <Button
              data-testid="all-filters-button"
              variant="ghost"
              onClick={() => onSecondaryFilterDrawerToggle(true)}
            >
              <Filters />
              <div className="sui-pl-2 sui-text-nowrap">
                <Typography weight="bold" color="none">All Filters</Typography>
              </div>
            </Button>
          )
        )}
        {
          primaryFiltersDimensions.map((dimension) => (
            renderPrimaryFiltersButton(dimension)
          ))
        }
      </div>
    );
  };

  return (
    <div data-component="PrimaryFilterDimensions">

      {renderFilterButtons(primaryFilters)}

      <PrimaryFiltersDrawer
        isOpen={isOpen}
        activeDimension={activeDimension}
        onCloseDrawer={onCloseDrawer}
        dimensions={dimensions}
        appliedDimensions={appliedDimensions}
        onDimensionChange={onRefinementChange}
        canonicalUrl={canonicalUrl}
        baseUrl={baseUrl}
      />
    </div>
  );
};

PrimaryFilters.propTypes = {
  baseUrl: string.isRequired,
  canonicalUrl: string.isRequired,
  dimensions: arrayOf(
    shape({
      label: string.isRequired
    })
  ).isRequired,
  primaryFilters: arrayOf(
    shape({
      label: string.isRequired
    })
  ).isRequired,
  appliedDimensions: arrayOf(shape({})).isRequired,
  activeDimension: shape({
    dimensionId: string,
    label: string,
    refinements: arrayOf(shape({}))
  }).isRequired,
  onRefinementChange: func.isRequired,
  onCloseDrawer: func.isRequired,
  onSecondaryFilterDrawerToggle: func.isRequired,
  hideViewAll: bool,
  isOpen: bool.isRequired
};

PrimaryFilters.defaultProps = {
  hideViewAll: false
};

PrimaryFilters.displayName = 'PrimaryFilters';

export { PrimaryFilters };
